/**
 * PURE DATA LAYER FOR THE REPORTS CHARTS. No React, no recharts, no DOM.
 *
 * WHY IT IS A SEPARATE MODULE. The rules that matter here are not visual, they are arithmetic
 * and they are about honesty: a bucket with no runs must produce `null` and never `0`, a
 * negative duration must not become a band, a group with one sample must not be presentable as
 * a rate. A test that greps `Reports.tsx` for `connectNulls` proves the prop is spelled; a test
 * that EXECUTES these functions proves the series actually carries a hole. This module exists
 * so the second kind of test is possible (see tests/test_reports_charts_gap_and_grouping.py,
 * which bundles it with esbuild and runs it under node).
 *
 * THE RULE THAT GOVERNS EVERY FUNCTION HERE: a chart must never imply data it does not have.
 * Concretely, and each of these is tested:
 *
 *   - An empty bucket yields `null` per series, NOT `0`. Zero seconds is a real and reachable
 *     measurement on this platform (a sub-100ms end-to-end rounds to 0.0, and the codebase
 *     already carries a regression test for exactly that conflation), so emitting `0` for "no
 *     runs" would draw the fastest possible transmission and the absence of any transmission as
 *     the same bar.
 *   - Buckets are EMITTED for the whole window including the empty ones, so a gap occupies its
 *     real width on the axis. Dropping empty buckets would slide two weeks of silence together
 *     and let a line be drawn across it, which reads as steady performance.
 *   - A NEGATIVE leg duration is not measured, it is a clock disagreement. It becomes `null`
 *     rather than a band, because a band drawn below the axis reads as a measurement.
 *   - `n` travels with every aggregate. A p90 over one sample is that one sample; the caller
 *     needs the denominator to say so rather than dressing a reading up as a distribution.
 *
 * WHAT IS DELIBERATELY EXCLUDED. `GET /api/runs` serves batch AGGREGATE rows alongside runs
 * (`kind: 'batch'`). Their timestamps and `quality_score` are aggregates over a batch's items,
 * not measurements of one fax, so mixing them into a per-fax distribution would weight one
 * batch of 50 equally with one fax. They are dropped here, once, rather than at each call site.
 *
 * WHAT THE PROVIDER OF A RUN MEANS HERE. A run's provider and method come from its JOB, per
 * LEG — a job may legitimately name a different provider on each leg. An ABSENT value is
 * rendered as its own group and never folded into the default provider: NULL means NOT
 * RECORDED, and attributing an unrecorded provider to eFax would report an inference as a
 * measurement. Group keys are raw values; labelling is the caller's job (`utils/methods.ts`
 * owns the label tables).
 */

/** The subset of a run row these functions read. Structural, so `Run` from the client fits. */
export interface RunLike {
  id: string
  job_id: string
  kind?: 'run' | 'batch' | null
  t_submitted?: string | null
  t_fax_sent?: string | null
  t_received?: string | null
  quality_score?: number | null
  created_at: string
}

/** The subset of a job these functions read: how each leg is configured. */
export interface JobFacet {
  id: string
  outbound_provider?: string | null
  inbound_provider?: string | null
  outbound_method?: string | null
  inbound_method?: string | null
}

export type GroupBy = 'none' | 'provider' | 'method' | 'provider-method'

/** `''` means "no filter". A filter matches when EITHER leg matches. */
export interface ChartFilters {
  provider: string
  method: string
}

/** Sentinel for an unrecorded leg value. Never the default provider — see module docstring. */
export const NOT_RECORDED = '(not recorded)'

export const ALL_GROUP = 'all'

export interface LegAggregate {
  /** Median. `null` means NOT MEASURED — never 0, which is a real reading. */
  p50: number | null
  p90: number | null
  /** How many measured samples the values above were computed over. 0 when none. */
  n: number
}

export interface TimeBucket {
  /** ISO8601 start of the bucket, half-open [start, start + size). */
  bucketStart: string
  /** Per group key: the two legs' medians for this bucket. */
  legs: Record<string, { outbound: number | null; inbound: number | null; n: number }>
}

export interface QualityBucket {
  bucketStart: string
  /** Per group key: mean quality score for this bucket, or null when nothing was scored. */
  scores: Record<string, { score: number | null; n: number }>
}

export interface DistributionRow {
  group: string
  outbound: LegAggregate
  inbound: LegAggregate
}

// ------------------------------------------------------------------ leg durations

function parseMs(value: string | null | undefined): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

/**
 * The two leg durations of one run, in seconds.
 *
 * Outbound is submit -> provider reported the fax sent. Inbound is fax sent -> received. A leg
 * whose either end is missing is NOT MEASURED (`null`); so is a leg whose ends disagree about
 * their order, because a negative duration is a clock fault and not a fast transmission.
 *
 * `0` is returned as `0` on purpose. It is the fastest reading the platform can produce and the
 * one an operator would most want to see; conflating it with `null` is a defect this repository
 * has already shipped once.
 */
export function legDurationsSeconds(run: RunLike): { outbound: number | null; inbound: number | null } {
  const submitted = parseMs(run.t_submitted)
  const sent = parseMs(run.t_fax_sent)
  const received = parseMs(run.t_received)

  let outbound: number | null = null
  if (submitted !== null && sent !== null && sent >= submitted) {
    outbound = (sent - submitted) / 1000
  }

  let inbound: number | null = null
  if (sent !== null && received !== null && received >= sent) {
    inbound = (received - sent) / 1000
  }

  return { outbound, inbound }
}

// ------------------------------------------------------------------ grouping

function legValue(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? NOT_RECORDED : trimmed
}

/**
 * The group key for a run, naming BOTH legs.
 *
 * `outbound -> inbound` rather than one collapsed value, because a job may name a different
 * provider or method per leg and the whole question this page answers is whether ONE leg
 * degraded. Collapsing the pair would hide the case the operator came here for.
 */
export function groupKeyFor(job: JobFacet | undefined, groupBy: GroupBy): string {
  if (groupBy === 'none') return ALL_GROUP
  const op = legValue(job?.outbound_provider)
  const ip = legValue(job?.inbound_provider)
  const om = legValue(job?.outbound_method)
  const im = legValue(job?.inbound_method)
  if (groupBy === 'provider') return `${op} → ${ip}`
  if (groupBy === 'method') return `${om} → ${im}`
  return `${op}/${om} → ${ip}/${im}`
}

/** A filter matches when EITHER leg matches it. `''` matches everything. */
export function matchesFilters(job: JobFacet | undefined, filters: ChartFilters): boolean {
  if (filters.provider) {
    const legs = [legValue(job?.outbound_provider), legValue(job?.inbound_provider)]
    if (!legs.includes(filters.provider)) return false
  }
  if (filters.method) {
    const legs = [legValue(job?.outbound_method), legValue(job?.inbound_method)]
    if (!legs.includes(filters.method)) return false
  }
  return true
}

/** Every provider value present across the given jobs' legs, sorted, unrecorded included. */
export function providerOptions(jobs: readonly JobFacet[]): string[] {
  const seen = new Set<string>()
  for (const job of jobs) {
    seen.add(legValue(job.outbound_provider))
    seen.add(legValue(job.inbound_provider))
  }
  return [...seen].sort()
}

/** Every method value present across the given jobs' legs, sorted, unrecorded included. */
export function methodOptions(jobs: readonly JobFacet[]): string[] {
  const seen = new Set<string>()
  for (const job of jobs) {
    seen.add(legValue(job.outbound_method))
    seen.add(legValue(job.inbound_method))
  }
  return [...seen].sort()
}

// ------------------------------------------------------------------ statistics

/** Nearest-rank percentile over a NON-EMPTY sorted array. */
function percentileSorted(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) throw new Error('percentileSorted requires at least one sample')
  const rank = Math.ceil((p / 100) * sorted.length)
  const index = Math.min(sorted.length - 1, Math.max(0, rank - 1))
  return sorted[index]
}

function aggregate(samples: number[]): LegAggregate {
  if (samples.length === 0) return { p50: null, p90: null, n: 0 }
  const sorted = [...samples].sort((a, b) => a - b)
  return {
    p50: round1(percentileSorted(sorted, 50)),
    p90: round1(percentileSorted(sorted, 90)),
    n: sorted.length,
  }
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

function mean(samples: number[]): number | null {
  if (samples.length === 0) return null
  return round1(samples.reduce((a, b) => a + b, 0) / samples.length)
}

// ------------------------------------------------------------------ bucketing

/**
 * Bucket width for a window, in milliseconds.
 *
 * Coarsens with the window so a 30-day view is ~30 bars rather than 720. Chosen by window
 * rather than by a target bar count so the same window always buckets the same way — two
 * operators comparing screens must not be looking at differently-shaped time.
 */
export function bucketSizeMs(hours: number): number {
  const HOUR = 3600_000
  if (hours <= 6) return 15 * 60_000
  if (hours <= 48) return HOUR
  if (hours <= 168) return 6 * HOUR
  return 24 * HOUR
}

/**
 * Every bucket start in [from, to), including the ones that will hold nothing.
 *
 * EMPTY BUCKETS ARE EMITTED. That is the whole point: a gap must occupy its real width on the
 * axis. If only populated buckets were emitted, a fortnight of silence would render as two
 * adjacent categories and any line across them would read as steady performance.
 */
export function bucketStarts(fromMs: number, toMs: number, sizeMs: number): number[] {
  if (sizeMs <= 0 || toMs <= fromMs) return []
  const first = Math.floor(fromMs / sizeMs) * sizeMs
  const out: number[] = []
  for (let t = first; t < toMs; t += sizeMs) out.push(t)
  return out
}

interface WindowSpec {
  fromMs: number
  toMs: number
  hours: number
}

function prepare(
  runs: readonly RunLike[],
  jobs: readonly JobFacet[],
  groupBy: GroupBy,
  filters: ChartFilters,
) {
  const byId = new Map(jobs.map(job => [job.id, job]))
  const kept: Array<{ run: RunLike; group: string }> = []
  for (const run of runs) {
    // Batch aggregate rows are not per-fax measurements — see module docstring.
    if (run.kind === 'batch') continue
    const job = byId.get(run.job_id)
    if (!matchesFilters(job, filters)) continue
    kept.push({ run, group: groupKeyFor(job, groupBy) })
  }
  const groups = [...new Set(kept.map(k => k.group))].sort()
  return { kept, groups }
}

/**
 * Per-leg medians over time, as one entry per bucket per group.
 *
 * The caller stacks `outbound` under `inbound` so the two legs are separately visible and their
 * sum is the end-to-end. A bucket a group has no measured run in carries `null` for both legs
 * and `n: 0`.
 */
export function buildTimeBands(
  runs: readonly RunLike[],
  jobs: readonly JobFacet[],
  window: WindowSpec,
  groupBy: GroupBy,
  filters: ChartFilters,
): { buckets: TimeBucket[]; groups: string[]; emptyBuckets: number } {
  const sizeMs = bucketSizeMs(window.hours)
  const starts = bucketStarts(window.fromMs, window.toMs, sizeMs)
  const { kept, groups } = prepare(runs, jobs, groupBy, filters)

  // bucketStart -> group -> per-leg samples
  const acc = new Map<number, Map<string, { out: number[]; in: number[]; n: number }>>()
  for (const { run, group } of kept) {
    const entryMs = parseMs(run.t_submitted) ?? parseMs(run.created_at)
    if (entryMs === null) continue
    const bucket = Math.floor(entryMs / sizeMs) * sizeMs
    if (!acc.has(bucket)) acc.set(bucket, new Map())
    const perGroup = acc.get(bucket)!
    if (!perGroup.has(group)) perGroup.set(group, { out: [], in: [], n: 0 })
    const cell = perGroup.get(group)!
    cell.n += 1
    const { outbound, inbound } = legDurationsSeconds(run)
    if (outbound !== null) cell.out.push(outbound)
    if (inbound !== null) cell.in.push(inbound)
  }

  let emptyBuckets = 0
  const buckets: TimeBucket[] = starts.map(start => {
    const perGroup = acc.get(start)
    if (!perGroup || perGroup.size === 0) emptyBuckets += 1
    const legs: TimeBucket['legs'] = {}
    for (const group of groups) {
      const cell = perGroup?.get(group)
      if (!cell) {
        // NOT ZERO. See the module docstring: zero seconds is a real reading.
        legs[group] = { outbound: null, inbound: null, n: 0 }
        continue
      }
      legs[group] = {
        outbound: cell.out.length ? aggregate(cell.out).p50 : null,
        inbound: cell.in.length ? aggregate(cell.in).p50 : null,
        n: cell.n,
      }
    }
    return { bucketStart: new Date(start).toISOString(), legs }
  })

  return { buckets, groups, emptyBuckets }
}

/**
 * Per-leg p50/p90 per group, for the distribution chart.
 *
 * A group with no MEASURED sample on either leg is OMITTED rather than emitted as zero: an
 * absent bar cannot be misread, a zero-height bar reads as an instant transmission.
 */
export function buildLegDistribution(
  runs: readonly RunLike[],
  jobs: readonly JobFacet[],
  groupBy: GroupBy,
  filters: ChartFilters,
): DistributionRow[] {
  const { kept, groups } = prepare(runs, jobs, groupBy, filters)
  const samples = new Map<string, { out: number[]; in: number[] }>()
  for (const group of groups) samples.set(group, { out: [], in: [] })
  for (const { run, group } of kept) {
    const cell = samples.get(group)!
    const { outbound, inbound } = legDurationsSeconds(run)
    if (outbound !== null) cell.out.push(outbound)
    if (inbound !== null) cell.in.push(inbound)
  }
  const rows: DistributionRow[] = []
  for (const group of groups) {
    const cell = samples.get(group)!
    if (cell.out.length === 0 && cell.in.length === 0) continue
    rows.push({ group, outbound: aggregate(cell.out), inbound: aggregate(cell.in) })
  }
  return rows
}

/**
 * Mean quality score per bucket per group.
 *
 * A bucket in which nothing was SCORED carries `null`, not 0 — a job with quality checking off
 * produces no score, and 0 is the worst possible score. Drawing "not checked" at the bottom of
 * the axis would invent the most alarming reading the chart can display.
 */
export function buildQualityTrend(
  runs: readonly RunLike[],
  jobs: readonly JobFacet[],
  window: WindowSpec,
  groupBy: GroupBy,
  filters: ChartFilters,
): { buckets: QualityBucket[]; groups: string[]; scoredRuns: number } {
  const sizeMs = bucketSizeMs(window.hours)
  const starts = bucketStarts(window.fromMs, window.toMs, sizeMs)
  const { kept, groups } = prepare(runs, jobs, groupBy, filters)

  const acc = new Map<number, Map<string, number[]>>()
  let scoredRuns = 0
  for (const { run, group } of kept) {
    if (run.quality_score === null || run.quality_score === undefined) continue
    const entryMs = parseMs(run.t_submitted) ?? parseMs(run.created_at)
    if (entryMs === null) continue
    scoredRuns += 1
    const bucket = Math.floor(entryMs / sizeMs) * sizeMs
    if (!acc.has(bucket)) acc.set(bucket, new Map())
    const perGroup = acc.get(bucket)!
    if (!perGroup.has(group)) perGroup.set(group, [])
    perGroup.get(group)!.push(run.quality_score)
  }

  const buckets: QualityBucket[] = starts.map(start => {
    const perGroup = acc.get(start)
    const scores: QualityBucket['scores'] = {}
    for (const group of groups) {
      const values = perGroup?.get(group)
      scores[group] = values && values.length
        ? { score: mean(values), n: values.length }
        : { score: null, n: 0 }
    }
    return { bucketStart: new Date(start).toISOString(), scores }
  })

  return { buckets, groups, scoredRuns }
}
